import type { Ref } from 'react'

type RefCleanup = () => void

/**
 * Merge multiple refs into a single ref callback. Its cleanup runs each ref's
 * returned cleanup and resets the refs that returned none.
 */
export function mergeRefs<T>(
  ...refs: (Ref<T> | undefined)[]
): (node: T | null) => RefCleanup {
  return (node) => {
    const cleanups = refs.map((ref) => assignRef(ref, node))
    return () => {
      cleanups.forEach((cleanup, index) => {
        if (cleanup) {
          cleanup()
        } else {
          assignRef(refs[index], null)
        }
      })
    }
  }
}

function assignRef<T>(
  ref: Ref<T> | undefined,
  node: T | null,
): RefCleanup | null {
  if (typeof ref === 'function') {
    const cleanup = ref(node)
    return typeof cleanup === 'function' ? cleanup : null
  }
  if (ref) {
    ref.current = node
  }
  return null
}
